import { FC, useMemo } from "react";
import { formatInTimeZone } from "date-fns-tz";

import {
  NOTIFICATION_ACTION_TYPES,
  createNotificationAction,
} from "shared/ui/notifications/notification-generic/base-parts/actions";
import { NOTIFICATION_TYPES } from "shared/lib/constants/notifications";
import { PropsWithClassNameI } from "shared/lib/interfaces/ui";

import { NotificationBase } from "@/modules/notifications/notification/base";
import {
  DialBlockAvailabilityNotificationI,
  NotificationI,
} from "@/lib/types/notification";
import { DIAL_BLOCK_ET_ZONE } from "@/modules/dial-blocks/lib/constants";
import { getEtInstant, isValidDayKey } from "@/modules/dial-blocks/lib/week";
import Router from "@/helpers/router";

type NotificationDialBlockAvailabilityPropsI = NotificationI &
  PropsWithClassNameI;

/** " for the week of Oct 26", or nothing when the week isn't a date. */
const forTheWeekOf = (weekStart: string | undefined) =>
  weekStart && isValidDayKey(weekStart)
    ? ` for the week of ${formatInTimeZone(
        getEtInstant(weekStart, 12 * 60),
        DIAL_BLOCK_ET_ZONE,
        "MMM d"
      )}`
    : "";

/**
 * The LGA's answer to an availability request, in Command Center's words
 * (`useAvailabilityUpdates.ts`).
 */
export const describeAvailabilityAnswer = (
  isApproved: boolean,
  answer: DialBlockAvailabilityNotificationI | undefined
): string => {
  const week = forTheWeekOf(answer?.week_start);
  const notes = answer?.review_notes?.trim();

  // A usual commitment request (C-12 B): from the week after the approval.
  // A number missing from the payload is left out, never shown as 0.
  if (answer?.kind === "usual") {
    const blocks = answer.blocks_per_week ?? answer.reduced_blocks;
    const perWeek = blocks === undefined ? "" : ` ${blocks} blocks a week`;

    if (isApproved) {
      const from = forTheWeekOf(answer.effective_week_start).replace(
        " for the week of ",
        " from the week of "
      );

      return `Your usual commitment change has been approved${
        perWeek ? `:${perWeek}` : ""
      }${from}.${notes ? ` Note: ${notes}` : ""}`;
    }

    return `Your request to change your usual commitment${
      perWeek ? ` to${perWeek}` : ""
    } was denied.${notes ? ` Reason: ${notes}` : ""}`;
  }

  if (isApproved) {
    const adjusted =
      answer?.reduced_blocks === undefined
        ? ""
        : ` Your commitment has been adjusted to ${answer.reduced_blocks} blocks.`;

    return `Your availability update${week} has been approved.${adjusted}${
      notes ? ` Note: ${notes}` : ""
    }`;
  }

  return `Your availability update${week} was denied.${
    notes ? ` Reason: ${notes}` : ""
  }`;
};

/**
 * DialBlockAvailabilityApproved / DialBlockAvailabilityDenied (GLE-8284):
 * the LGA approved or denied the caller's request to lower one week's
 * commitment. Opens My Week on that week.
 */
export const NotificationDialBlockAvailability: FC<
  NotificationDialBlockAvailabilityPropsI
> = (props) => {
  const {
    id,
    is_seen,
    created_at,
    className,
    campaign_name,
    notification_type,
    dial_block_availability,
  } = props;

  const text = useMemo(
    () =>
      describeAvailabilityAnswer(
        notification_type ===
          NOTIFICATION_TYPES.DIAL_BLOCK_AVAILABILITY_APPROVED,
        dial_block_availability
      ),
    [notification_type, dial_block_availability]
  );

  const week = dial_block_availability?.week_start;

  return (
    <NotificationBase
      id={id}
      isUnread={!is_seen}
      className={className}
      avatar={props}
      description={{
        text: <>{text}</>,
        date: created_at,
        campaignName: campaign_name,
      }}
      actions={[
        createNotificationAction(
          NOTIFICATION_ACTION_TYPES.PRIMARY,
          "View my week",
          () => {
            window.location.href = Router.myWeek(
              week && isValidDayKey(week) ? week : undefined
            );
          }
        ),
      ]}
    />
  );
};
